import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '../common';

export interface SuggestionItem {
  content: string;
  rationale?: string;
}

export interface TaggedSuggestion {
  content: string;
  rationale?: string;
  tag: string;
}

export interface ExclusionSuggestions {
  commonPitfalls: SuggestionItem[];
  keepFocus: SuggestionItem[];
}

const TAG_COLORS: Record<string, string> = {
  core_topic: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  continues_previous: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  provocative_thought: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  conflict: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  turning_point: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  personal_story: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  personal_memory: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  personal_success: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  personal_failure: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  scientific_fact: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  contradicting_claim: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

function TagBadge({ tag }: { tag: string }) {
  const { t } = useTranslation();
  const color = TAG_COLORS[tag] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  const label = t(`suggestions.tags.${tag}`, tag);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function TaggedSuggestionCard({
  item,
  onAdd,
  isAdding,
  isAdded,
  onMoreOfTag,
  isLoadingMore,
}: {
  item: TaggedSuggestion;
  onAdd: () => void;
  isAdding: boolean;
  isAdded: boolean;
  onMoreOfTag: () => void;
  isLoadingMore: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={`flex flex-col gap-1.5 p-2.5 border rounded-lg transition-all duration-300 ${
      isAdded
        ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 opacity-60'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
    }`}>
      <div className="flex items-center justify-between gap-2">
        <TagBadge tag={item.tag} />
        <div className="flex items-center gap-1 flex-shrink-0">
          {isAdded ? (
            <span className="flex items-center gap-1 px-2 py-1 text-xs text-green-600 dark:text-green-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('suggestions.added')}
            </span>
          ) : (
            <>
              <button
                onClick={onAdd}
                disabled={isAdding}
                className="p-1.5 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded transition-colors disabled:opacity-50"
                title={t('suggestions.add')}
              >
                {isAdding ? (
                  <Spinner size="xs" />
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                )}
              </button>
              <button
                onClick={onMoreOfTag}
                disabled={isLoadingMore}
                className="px-1.5 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50 flex items-center gap-1"
                title={t('suggestions.moreLikeThis')}
              >
                {isLoadingMore ? (
                  <Spinner size="xs" />
                ) : (
                  <>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t('suggestions.moreLikeThis')}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
      <p className="text-sm text-gray-800 dark:text-gray-200">{item.content}</p>
      {item.rationale && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">{item.rationale}</p>
      )}
    </div>
  );
}

// Shared category section component for exclusion suggestions
function CategorySection({
  icon,
  title,
  subtitle,
  items,
  onAddItem,
  addingIndex,
  addedIndices,
}: {
  icon: string;
  title: string;
  subtitle: string;
  items: SuggestionItem[];
  onAddItem: (index: number) => void;
  addingIndex: number | null;
  addedIndices: Set<number>;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-base">{icon}</span>
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-2 ml-6">
        {items.map((item, index) => {
          const isAdded = addedIndices.has(index);
          return (
            <div key={index} className={`flex items-start gap-2 p-2 border rounded-lg transition-all duration-300 ${
              isAdded
                ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 opacity-60'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
            }`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800 dark:text-gray-200">{item.content}</p>
                {item.rationale && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">{item.rationale}</p>
                )}
              </div>
              {isAdded ? (
                <span className="flex items-center gap-1 flex-shrink-0 px-2 py-1 text-xs text-green-600 dark:text-green-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t('suggestions.added')}
                </span>
              ) : (
                <button
                  onClick={() => onAddItem(index)}
                  disabled={addingIndex === index}
                  className="flex-shrink-0 p-1.5 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded transition-colors disabled:opacity-50"
                  title={t('suggestions.add')}
                >
                  {addingIndex === index ? (
                    <Spinner size="xs" />
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Main panel wrapper
function SuggestionPanelWrapper({
  isOpen,
  onClose,
  title,
  isLoading,
  onRefresh,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  isLoading: boolean;
  onRefresh: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Spinner size="lg" />
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                {t('suggestions.generating', 'Generating suggestions...')}
              </p>
            </div>
          ) : (
            children
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="secondary"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading}
            leftIcon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            }
          >
            {t('suggestions.getMore', 'Get More Suggestions')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export type SuggestionMode = 'fresh' | 'continue' | 'mix';

interface ClaimSuggestionPanelProps {
  isOpen: boolean;
  onClose: () => void;
  suggestions: TaggedSuggestion[];
  isLoading: boolean;
  onAddSuggestion: (content: string) => Promise<void>;
  onRefreshSuggestions: () => void;
  onMoreOfTag: (tag: string) => void;
  isLoadingMore: boolean;
  loadingMoreTag: string | null;
  mode?: SuggestionMode;
  onChangeMode?: (mode: SuggestionMode) => void;
}

function SuggestionModeControl({
  mode,
  onChange,
  disabled,
}: {
  mode: SuggestionMode;
  onChange: (mode: SuggestionMode) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const options: { key: SuggestionMode; label: string; hint: string }[] = [
    {
      key: 'fresh',
      label: t('suggestions.modes.fresh', 'Fresh ideas'),
      hint: t('suggestions.modes.freshHint', 'Ignore the draft; surprise me'),
    },
    {
      key: 'continue',
      label: t('suggestions.modes.continue', 'Build on what’s here'),
      hint: t('suggestions.modes.continueHint', 'Pick up from the current prose'),
    },
    {
      key: 'mix',
      label: t('suggestions.modes.mix', 'Mix'),
      hint: t('suggestions.modes.mixHint', 'Half fresh, half continuation'),
    },
  ];
  const activeHint = options.find((o) => o.key === mode)?.hint;
  return (
    <div className="space-y-1.5">
      <div
        className="inline-flex w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-0.5"
        role="tablist"
        aria-label={t('suggestions.modes.label', 'Suggestion direction')}
      >
        {options.map((opt) => {
          const isActive = opt.key === mode;
          return (
            <button
              key={opt.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={disabled}
              onClick={() => onChange(opt.key)}
              className={`flex-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                isActive
                  ? 'bg-white dark:bg-gray-900 text-primary-700 dark:text-primary-300 shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {activeHint && (
        <p className="text-xs text-gray-500 dark:text-gray-400 px-1">{activeHint}</p>
      )}
    </div>
  );
}

interface ExclusionSuggestionPanelProps {
  isOpen: boolean;
  onClose: () => void;
  suggestions: ExclusionSuggestions | null;
  isLoading: boolean;
  onAddSuggestion: (content: string) => Promise<void>;
  onRefreshSuggestions: () => void;
}

// Claim Suggestions Panel
export function ClaimSuggestionPanel({
  isOpen,
  onClose,
  suggestions,
  isLoading,
  onAddSuggestion,
  onRefreshSuggestions,
  onMoreOfTag,
  isLoadingMore,
  loadingMoreTag,
  mode,
  onChangeMode,
}: ClaimSuggestionPanelProps) {
  const { t } = useTranslation();
  const [addingIndex, setAddingIndex] = useState<number | null>(null);
  const [addedIndices, setAddedIndices] = useState<Set<number>>(new Set());

  const handleAddItem = useCallback(async (index: number, content: string) => {
    setAddingIndex(index);
    try {
      await onAddSuggestion(content);
      setAddedIndices(prev => new Set(prev).add(index));
    } finally {
      setAddingIndex(null);
    }
  }, [onAddSuggestion]);

  const handleRefresh = useCallback(() => {
    setAddedIndices(new Set());
    onRefreshSuggestions();
  }, [onRefreshSuggestions]);

  const handleModeChange = useCallback(
    (next: SuggestionMode) => {
      if (!onChangeMode) return;
      setAddedIndices(new Set());
      onChangeMode(next);
    },
    [onChangeMode]
  );

  return (
    <SuggestionPanelWrapper
      isOpen={isOpen}
      onClose={onClose}
      title={t('suggestions.claimTitle', 'Claim Suggestions')}
      isLoading={isLoading}
      onRefresh={handleRefresh}
    >
      {mode && onChangeMode && (
        <div className="mb-3">
          <SuggestionModeControl mode={mode} onChange={handleModeChange} disabled={isLoading} />
        </div>
      )}
      {suggestions.length > 0 ? (
        <div className="space-y-2">
          {suggestions.map((item, index) => (
            <TaggedSuggestionCard
              key={`${item.tag}-${index}`}
              item={item}
              onAdd={() => handleAddItem(index, item.content)}
              isAdding={addingIndex === index}
              isAdded={addedIndices.has(index)}
              onMoreOfTag={() => onMoreOfTag(item.tag)}
              isLoadingMore={isLoadingMore && loadingMoreTag === item.tag}
            />
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">
          {t('suggestions.noSuggestions', 'No suggestions available. Try adding some claims first.')}
        </p>
      )}
    </SuggestionPanelWrapper>
  );
}

// Exclusion Suggestions Panel
export function ExclusionSuggestionPanel({
  isOpen,
  onClose,
  suggestions,
  isLoading,
  onAddSuggestion,
  onRefreshSuggestions,
}: ExclusionSuggestionPanelProps) {
  const { t } = useTranslation();
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [addedKeys, setAddedKeys] = useState<Set<string>>(new Set());

  const handleAddItem = useCallback(async (category: string, index: number, content: string) => {
    const key = `${category}-${index}`;
    setAddingKey(key);
    try {
      await onAddSuggestion(content);
      setAddedKeys(prev => new Set(prev).add(key));
    } finally {
      setAddingKey(null);
    }
  }, [onAddSuggestion]);

  const getAddingIndex = (category: string): number | null => {
    if (!addingKey) return null;
    const [cat, idx] = addingKey.split('-');
    return cat === category ? parseInt(idx, 10) : null;
  };

  const getAddedIndices = (category: string): Set<number> => {
    const indices = new Set<number>();
    addedKeys.forEach(key => {
      const [cat, idx] = key.split('-');
      if (cat === category) indices.add(parseInt(idx, 10));
    });
    return indices;
  };

  const handleRefresh = useCallback(() => {
    setAddedKeys(new Set());
    onRefreshSuggestions();
  }, [onRefreshSuggestions]);

  return (
    <SuggestionPanelWrapper
      isOpen={isOpen}
      onClose={onClose}
      title={t('suggestions.exclusionTitle', 'Exclusion Suggestions')}
      isLoading={isLoading}
      onRefresh={handleRefresh}
    >
      {suggestions && (
        <div className="space-y-6">
          <CategorySection
            icon="+"
            title={t('suggestions.commonPitfalls', 'Common pitfalls')}
            subtitle={t('suggestions.commonPitfallsDesc', 'Things writers often over-include')}
            items={suggestions.commonPitfalls}
            onAddItem={(idx) => handleAddItem('pitfalls', idx, suggestions.commonPitfalls[idx].content)}
            addingIndex={getAddingIndex('pitfalls')}
            addedIndices={getAddedIndices('pitfalls')}
          />
          <CategorySection
            icon="+"
            title={t('suggestions.keepFocus', 'Keep focus')}
            subtitle={t('suggestions.keepFocusDesc', 'Avoid distractions from the narrative')}
            items={suggestions.keepFocus}
            onAddItem={(idx) => handleAddItem('focus', idx, suggestions.keepFocus[idx].content)}
            addingIndex={getAddingIndex('focus')}
            addedIndices={getAddedIndices('focus')}
          />
          {suggestions.commonPitfalls.length === 0 && suggestions.keepFocus.length === 0 && (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              {t('suggestions.noExclusionSuggestions', 'No exclusion suggestions available.')}
            </p>
          )}
        </div>
      )}
    </SuggestionPanelWrapper>
  );
}

export default ClaimSuggestionPanel;
